import { Controller } from 'stimulus'
import { ScrollyAmortizationTable } from 'classes/scrolly-amortization-table'

import * as MortMath from 'modules/mort-math'
import * as MortDataPrep from 'modules/mort-data-prep'

import moment from 'moment'

import * as MomentHelp from 'helpers/moment-helper'
import parseFloatString from 'helpers/parse-float-string'
import formatCurrency from 'helpers/new-format-currency'

/**
 * Manages js for the extra payments page (StimulusJS controller)
 *
 * @class ExtraPaymentsController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'loanAmount',
    'inputs',
    'interestRatePercent',
    'loanLength',
    'loanStartDate',
    'additionalPaymentStartDate',
    'additionalMonthlyPayment',
    'standardMonthlyPayment',
    'newMonthlyPayment',
    'totalSavings',
    'paidOffSchedule',
    'amortizationTable',
    'additionalMonthlyPaymentResult',
    'interestSavingsResult',
    'paidOffScheduleYearResult',
    'paidOffScheduleMonthResult',
    'toggleAmortizationTable',
    'resultsData',
    'dateBox'
  ]

  timeout = null

  /**
   * @function Controllers.ExtraPaymentsController.initialize
   * @memberof Controllers
   *
   * @description Called on page load. Calculates two mortgage schedules for comparison, one with an additional payment and one without
   */
  initialize() {
    this.termToggle = 'monthly'

    this._forceAdditionalDateConstraint()

    const {
      originalMonthlyPayment,
      newMonthlyPayment,
      lifetimeSavings,
      yearDiff,
      monthDiff,
      additionalPayment,
      originalSchedule,
      newSchedule,
      nextMonth
    } = this._recalculateData()

    this._displayTextualData({ originalMonthlyPayment, newMonthlyPayment, lifetimeSavings, yearDiff, monthDiff, additionalPayment })

    const amortizationComparisonTableData = MortDataPrep.monthlyComparisonTableData(originalSchedule, newSchedule, nextMonth, { useFiltered: false })

    if (this.hasAmortizationTableTarget) {
      this.amortizationTable = new ScrollyAmortizationTable(this.amortizationTableTarget)
      this.amortizationTable.buildDeferred(this.amortizationTableTarget, amortizationComparisonTableData)
    }
  }


  /**
   * @function Controllers.ExtraPaymentsController.process
   * @memberof Controllers
   *
   * @description Called on `keyup` event and `toggle()`. Recalculates the comparison loans and potentially changes the table to yearly.
   */
  process() {
    clearTimeout(this.timeout)

    if (this.hasAmortizationTableTarget) {
      this.amortizationTableTarget.dispatchEvent(new CustomEvent('table:collapse'))
    }

    this.timeout = setTimeout(() => {
      this._forceAdditionalDateConstraint()

      if (!this.hasStartDates) { return false }

      const {
        originalMonthlyPayment,
        newMonthlyPayment,
        lifetimeSavings,
        yearDiff,
        monthDiff,
        additionalPayment,
        originalSchedule,
        newSchedule,
        nextMonth
      } = this._recalculateData()

      this._displayTextualData({ originalMonthlyPayment, newMonthlyPayment, lifetimeSavings, yearDiff, monthDiff, additionalPayment })

      let amortizationComparisonTableData

      if (this.termToggle === 'annually') {
        const yearlyOriginalSchedule    = MortMath.yearlyAmortizationSchedule(originalSchedule)
        const yearlyNewSchedule         = MortMath.yearlyAmortizationSchedule(newSchedule)
        amortizationComparisonTableData = MortDataPrep.yearlyComparisonTableData(yearlyOriginalSchedule, yearlyNewSchedule, nextMonth, { useFiltered: false })
      } else {
        amortizationComparisonTableData = MortDataPrep.monthlyComparisonTableData(originalSchedule, newSchedule, nextMonth, { useFiltered: false })
      }

      if (this.hasAmortizationTableTarget) {
        this.amortizationTable.build(amortizationComparisonTableData)
      }
    }, 300)
  }

  /**
   * @function Controllers.ExtraPaymentsController.hideButton
   * @memberof Controllers
   * @description Changes button color and text. Called on `click` event
   *
   * @param {Event} event - Event from user interaction
   *
   * @return {undefined}
   */
  hideButton(event) {
    event.target.classList.add('button--desktop-hidden')
  }

  /**
   * @function Controllers.ExtraPaymentsController.toggleResults
   * @memberof Controllers
   * @description Toggles the visibility of calculator results
   *
   * @param {Event} event - Event from user interaction
   *
   * @return {undefined}
   */
  toggleResults(event) {
    const hidden = 'form__fields--hidden'
    const mobileHidden = 'form__fields--mobile-hidden'
    const cssClass = 'mortgage-calculator__edit-inputs-btn'

    if (!event.target.classList.contains(cssClass)) {
      this.resultsDataTarget.classList.remove(hidden, mobileHidden)
      event.target.classList.add(cssClass)
      this.inputsTarget.classList.add(mobileHidden)
    } else {
      this.resultsDataTarget.classList.add(mobileHidden)
      event.target.classList.remove(cssClass)
      this.inputsTarget.classList.remove(mobileHidden)
    }
  }

  /**
   * @function Controllers.ExtraPaymentsController.toggle
   * @memberof Controllers
   * @description Toggles graph between monthly and yearly. Called on `click` event
   *
   * @param {Event} event - Event from user interaction
   *
   * @return {undefined} Causes the table to be redrawn with yearly or monthly data
   */
  toggle(event) {
    const toggleButtons = document.querySelectorAll(`[data-target="${this.toggleAmortizationTableTarget.dataset.target}"]`)

    Array.from(toggleButtons).map(button => {
      button.classList.remove('mortgage-calculator__amortization-information-toggle-button--active')
    })

    event.target.classList.add('mortgage-calculator__amortization-information-toggle-button--active')

    // Sets toggle to "monthly" or "annually".
    this.termToggle = event.target.dataset.value

    this.process()
  }


  // private

  /**
   * @function Controllers.ExtraPaymentsController._recalculateData
   * @memberof Controllers
   * @description A phoenix rises from the ashes of calculations past... Takes inputs from the UI and gets calculations from MortMath
   * @private
   *
   * @return {Object}
   *
   * @example
   *
   * return = {
   *   originalMonthlyPayment: 1453.33434...,
   *   newMonthlyPayment:      1608.84323...,
   *   lifetimeSavings:        43173.4333...,
   *   yearDiff:               5,
   *   monthDiff:              1,
   *   additionalPayment:      155,
   *   originalSchedule:       schedule object, - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
   *   newSchedule:            schedule object, - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
   *   nextMonth:              moment of the upcoming month,
   * }
   *
   */
  _recalculateData() {
    const nextMonth = moment().add(1, 'M')
    const { originalDetails, newDetails, originalMonthlyPayment, newMonthlyPayment, additionalPayment } = this._prepareFormValues()

    const originalSchedule = MortMath.amortizationSchedule(originalDetails)
    const newSchedule      = MortMath.amortizationSchedule(newDetails)

    const originalTotalInterestFromNextMonth = MortMath.totalInterestFrom(originalSchedule, nextMonth)
    const newTotalInterestFromNextMonth      = MortMath.totalInterestFrom(newSchedule, nextMonth)

    const lifetimeSavings         = originalTotalInterestFromNextMonth - newTotalInterestFromNextMonth
    const { yearDiff, monthDiff } = MortDataPrep.loanEndDifference(originalSchedule, newSchedule)

    return {
      originalMonthlyPayment,
      newMonthlyPayment,
      lifetimeSavings,
      yearDiff,
      monthDiff,
      additionalPayment,
      originalSchedule,
      newSchedule,
      nextMonth
    }
  }

  /**
   * @function Controllers.ExtraPaymentsController._prepareFormValues
   * @memberof Controllers
   * @description Takes inputs and prepares them for the creation of MortMath schedules.
   * @private
   *
   * @return {Object} - originalDetails and originalMonthlyPayment
   *
   * @example
   *
   * return = {
   *   originalDetails: {
   *     date:                    moment,
   *     monthlyInterestPercent:  0.0034..., - result of [MortMath.monthlyInterestPercent]{@link module:MortMath.monthlyInterestPercent}
   *     monthlyPayment:          1453.949..., - result of [MortMath.monthlyPayment]{@link module:MortMath.monthlyPayment}
   *     balance:                 300000,
   *     additionalPaymentAmount: 0,
   *   },
   *   newDetails {
   *     date:                    moment,
   *     monthlyInterestPercent:  0.0034..., - result of [MortMath.monthlyInterestPercent]{@link module:MortMath.monthlyInterestPercent}
   *     monthlyPayment:          1453.949..., - result of [MortMath.monthlyPayment]{@link module:MortMath.monthlyPayment}
   *     balance:                 300000,
   *     additionalPaymentAmount: 0,
   *     additionalPaymentStart:  moment,
   *   },
   *   originalMonthlyPayment: 1453.949..., - result of [MortMath.monthlyPayment]{@link module:MortMath.monthlyPayment}
   *   newMonthlyPayment:      1608.949..., - the original payment + the additional payment
   *   additionalPayment:      parsedAdditionalPayment,
   * }
   */
  _prepareFormValues() {
    const parsedLoanTerm                   = parseFloatString(this.loanLengthTarget.value)
    const parsedLoanAmount                 = parseFloatString(this.loanAmountTarget.value)
    const parsedInterestPercent            = parseFloatString(this.interestRatePercentTarget.value)
    const parsedAdditionalPayment          = parseFloatString(this.additionalMonthlyPaymentTarget.value) || 0
    const parsedAdditionalPaymentStartDate = MomentHelp.formatForMomentMonthFirst(this.additionalPaymentStartDateTarget.value)
    const parsedLoanStartDate              = MomentHelp.formatForMomentMonthFirst(this.loanStartDateTarget.value)

    const loanTermInMonths           = parsedLoanTerm * 12
    const monthlyInterestPercent     = MortMath.monthlyInterestPercent(parsedInterestPercent)
    const loanStartDate              = moment(parsedLoanStartDate)
    const additionalPaymentStartDate = moment(parsedAdditionalPaymentStartDate)

    const monthlyPayment = MortMath.monthlyPayment({
      principal:              parsedLoanAmount,
      monthlyInterestPercent: monthlyInterestPercent,
      loanTermInMonths:       loanTermInMonths
    })

    const originalDetails = {
      date:                    loanStartDate,
      monthlyInterestPercent:  monthlyInterestPercent || 0,
      monthlyPayment:          monthlyPayment || 0,
      balance:                 parsedLoanAmount || 0,
      additionalPaymentAmount: 0
    }

    const newDetails = {
      date:                    loanStartDate,
      monthlyInterestPercent:  monthlyInterestPercent || 0,
      monthlyPayment:          monthlyPayment || 0,
      balance:                 parsedLoanAmount || 0,
      additionalPaymentStart:  additionalPaymentStartDate,
      additionalPaymentAmount: parsedAdditionalPayment || 0
    }

    return {
      originalDetails:        originalDetails,
      newDetails:             newDetails,
      originalMonthlyPayment: monthlyPayment,
      newMonthlyPayment:      monthlyPayment + parsedAdditionalPayment,
      additionalPayment:      parsedAdditionalPayment
    }
  }

  /**
   * @function Controllers.ExtraPaymentsController._displayTextualData
   * @memberof Controllers
   * @description Passes the display values along to two functions responsible for setting the DOM in two areas.
   * @private
   *
   * @param {Float} originalMonthlyPayment
   * @param {Float} newMonthlyPayment
   * @param {Float} lifetimeSavings
   * @param {Integer} yearDiff
   * @param {Integer} monthDiff
   * @param {Integer} additionalPayment
   *
   * @return {undefined} Manipulates DOM
   */
  _displayTextualData({ originalMonthlyPayment, newMonthlyPayment, lifetimeSavings, yearDiff, monthDiff, additionalPayment }) {
    this._displayResultPanelData({ originalMonthlyPayment, newMonthlyPayment, lifetimeSavings, yearDiff, monthDiff })

    if (this.hasAdditionalMonthlyPaymentResultTarget) {
      this._displayBannerText({ additionalPayment, lifetimeSavings, yearDiff, monthDiff })
    }
  }

  /**
   * @function Controllers.ExtraPaymentsController._displayResultPanelData
   * @memberof Controllers
   * @description Sets values in the DOM for the results panel
   * @private
   *
   * @param {Float} originalMonthlyPayment
   * @param {Float} newMonthlyPayment
   * @param {Float} lifetimeSavings
   * @param {Integer} yearDiff
   * @param {Integer} monthDiff
   *
   * @return {undefined} Manipulates DOM
   */
  _displayResultPanelData({ originalMonthlyPayment, newMonthlyPayment, lifetimeSavings, yearDiff, monthDiff }) {
    this.newMonthlyPaymentTarget.innerHTML      = formatCurrency(newMonthlyPayment)
    this.standardMonthlyPaymentTarget.innerHTML = formatCurrency(originalMonthlyPayment)
    this.totalSavingsTarget.innerHTML           = formatCurrency(lifetimeSavings)
    this.paidOffScheduleTarget.innerHTML        = `${yearDiff} yrs and ${monthDiff} mos. earlier`
  }

  /**
   * @function Controllers.ExtraPaymentsController._displayBannerText
   * @memberof Controllers
   * @description Sets values in the DOM for the banner text
   * @private
   *
   * @param {Integer} additionalPayment
   * @param {Float} lifetimeSavings
   * @param {Integer} yearDiff
   * @param {Integer} monthDiff
   *
   * @return {undefined} Manipulates DOM
   */
  _displayBannerText({ additionalPayment, lifetimeSavings, yearDiff, monthDiff }) {
    this.additionalMonthlyPaymentResultTarget.textContent = formatCurrency(additionalPayment)
    this.interestSavingsResultTarget.textContent          = formatCurrency(lifetimeSavings)
    this.paidOffScheduleYearResultTarget.textContent      = yearDiff
    this.paidOffScheduleMonthResultTarget.textContent     = monthDiff
  }


  /**
   * @function Controllers.ExtraPaymentsController._forceAdditionalDateConstraint
   * @memberof Controllers
   * @description If the additional payment start date is less than the original start date of
   * the loan, enforce the original
   *
   * @private
   */
  _forceAdditionalDateConstraint() {
    if (this.loanStartDateTarget.value.length >= 7 && this.additionalPaymentStartDateTarget.value.length >= 7) {
      const startDateArr = this.loanStartDateTarget.value.split('/')
      const startDateMonth = parseInt(startDateArr[0])
      const startDateYear = parseInt(startDateArr[1])
      const additionalPaymentStartDateArr = this.additionalPaymentStartDateTarget.value.split('/')
      const additionalPaymentStartDateMonth = parseInt(additionalPaymentStartDateArr[0])
      const additionalPaymentStartDateYear = parseInt(additionalPaymentStartDateArr[1])

      // Month and year number comparison
      if (startDateYear > additionalPaymentStartDateYear ||
        (startDateYear === additionalPaymentStartDateYear && startDateMonth > additionalPaymentStartDateMonth)) {
        this.additionalPaymentStartDateTarget.value = this.loanStartDateTarget.value
        this.additionalPaymentStartDateTarget.dispatchEvent(new CustomEvent('keyup'))
      }
    }
  }


  /**
  * @function Controllers.ExtraPaymentsController.hasStartDates
  * @memberof Controllers
  * @description Validates that loanStartDate and additionalPaymentStartDate have 7 digits.
  * @private
  *
  * @return {boolean}
  */
  get hasStartDates() {
    return this.loanStartDateTarget.value.length === 7 &&
           this.additionalPaymentStartDateTarget.value.length === 7
  }
}
